import {
  Component,
  inject,
  signal,
  computed,
  input,
  output,
  OnInit,
  effect
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { CatalogoRepository } from 'src/app/domain/service/catalogo/catalogo-repository';
import { CatalogoOption } from 'src/app/domain/model/catalogo/catalogo';
import { ProgramacionServicio } from 'src/app/domain/model/agenda/agenda';
import { sanitizeText, sanitizeTextarea } from 'src/app/common/input-utils';
import { environment } from 'src/environments/environment';

/**
 * Resultado del diálogo de cancelación
 */
export interface CancelarCitaResult {
  citaId: number;
  codigoTipoCancelador: string;
  descripcionTipoCancelador: string;
  nombreContacto?: string;
  observaciones?: string;
  /** Motivo de cancelación (TPSC_PARAMETROS_GENERALES.DSPARAMETRO donde DSVALOR='stMotivo') */
  motivoCancelacion?: string;
  /** Justificación cumplimiento tardío - solo para BES (TPSC_JUSTIFICACIONES) */
  codigoJustificacion?: string;
  descripcionJustificacion?: string;
  /**
   * Flag para cancelar citas futuras en programación periódica.
   * Legacy: ivasncancelarfuturas (SP_CANCELAR_CITA)
   * 'S' = Cancelar todas las citas futuras asociadas
   * 'N' = Solo cancelar esta cita (default)
   */
  cancelarFuturas?: 'S' | 'N';
  /**
   * Consecutivo del usuario/asesor que cancela.
   * Legacy: inuconsecutivousuario - cdpersona del asesor (para ASE) o persona que cancela
   */
  consecutivoUsuario?: number;
}

/**
 * Datos de persona para mostrar en el panel
 * Legacy: IdentificationViewImpl
 */
export interface DatosPersona {
  tipoIdentificacion?: string;
  numeroIdentificacion?: string;
  nombre1?: string;
  nombre2?: string;
  apellido1?: string;
  apellido2?: string;
  /** Nombre completo pre-formateado (se usa si no hay datos separados) */
  nombreCompleto?: string;
}

/**
 * Tipos de cancelador según legacy (TPSC_TIPOS_CANC_CITAS):
 * Los códigos pueden variar entre ambientes (ASE/CON para Asesor/Consultor, PRO para Programador)
 * Por eso usamos funciones que verifican por descripción además del código.
 *
 * - CONSULTOR/ASESOR - muestra datos del asesor de la cita
 * - PROGRAMADOR - muestra datos del usuario en sesión
 * - CLIENTE ANTICIPADO/BENEFICIARIO - requiere contacto
 * - CLIENTE EN SITIO/BENEFICIARIO EN SITIO - requiere contacto y justificación
 * - INTERNO - requiere contacto
 */

/** Verifica si el tipo es ASESOR/CONSULTOR - muestra panel de persona del asesor */
function esTipoAsesor(codigo: string | null, descripcion?: string): boolean {
  if (!codigo) return false;
  const codigoUpper = codigo.toUpperCase();
  const descUpper = descripcion?.toUpperCase() || '';
  return (
    codigoUpper === 'ASE' ||
    codigoUpper === 'CON' ||
    descUpper.includes('ASESOR') ||
    descUpper.includes('CONSULTOR')
  );
}

/** Verifica si el tipo es PROGRAMADOR - muestra panel de persona del usuario */
function esTipoProgramador(
  codigo: string | null,
  descripcion?: string
): boolean {
  if (!codigo) return false;
  const codigoUpper = codigo.toUpperCase();
  const descUpper = descripcion?.toUpperCase() || '';
  return codigoUpper === 'PRO' || descUpper.includes('PROGRAMADOR');
}

/** Verifica si el tipo es CLIENTE (ANTICIPADO o EN SITIO) - muestra datos del beneficiario */
function esTipoCliente(codigo: string | null, descripcion?: string): boolean {
  if (!codigo) return false;
  const codigoUpper = codigo.toUpperCase();
  const descUpper = descripcion?.toUpperCase() || '';
  // Tipos legacy: BEN (beneficiario anticipado), BES (beneficiario sitio)
  // Tipos actuales: CLIENTE ANTICIPADO, CLIENTE EN SITIO
  return (
    ['BEN', 'BES'].includes(codigoUpper) ||
    descUpper.includes('CLIENTE ANTICIPADO') ||
    descUpper.includes('CLIENTE EN SITIO') ||
    descUpper.includes('BENEFICIARIO ANTICIPADO') ||
    descUpper.includes('BENEFICIARIO EN SITIO')
  );
}

/** Verifica si el tipo requiere nombre de contacto (BEN, BES, INT, o tipos de cliente) */
function requiereNombreContacto(
  codigo: string | null,
  descripcion?: string
): boolean {
  if (!codigo) return false;
  const codigoUpper = codigo.toUpperCase();
  const descUpper = descripcion?.toUpperCase() || '';
  // Tipos legacy: BEN (beneficiario), BES (beneficiario sitio), INT (interno)
  // Tipos actuales: pueden tener descripciones como "CLIENTE ANTICIPADO", "CLIENTE EN SITIO"
  return (
    ['BEN', 'BES', 'INT'].includes(codigoUpper) ||
    descUpper.includes('CLIENTE') ||
    descUpper.includes('BENEFICI') ||
    descUpper.includes('INTERNO')
  );
}

/**
 * CancelarCitaDialogComponent
 *
 * Diálogo para cancelar citas - replica CancelServiceAppointmentInterfaceImpl.java
 *
 * Estructura legacy:
 * - Título: "Cancelar programación"
 * - Botones: "Grabar" | "Cerrar"
 * - ¿Quien realiza la cancelación? (dropdown)
 * - Panel de Persona (tipo ID, número, nombres, apellidos) - según tipo seleccionado
 * - Nombre del contacto (solo para BEN, BES, INT)
 * - Motivo de cancelación (dropdown de justificaciones)
 * - Observación (textarea obligatorio)
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Component({
  selector: 'app-cancelar-cita-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    InputTextModule,
    InputTextarea
  ],
  template: `
    <p-dialog
      [(visible)]="visible"
      header="Cancelar programación"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '50rem' }"
      [contentStyle]="{ 'max-height': '80vh', 'overflow-y': 'auto' }"
      [breakpoints]="{ '1024px': '88vw', '768px': '92vw', '640px': '96vw' }"
      [closable]="true"
      (onHide)="onClose.emit()"
    >
      <!-- Subtítulo: Datos de la cancelación -->
      <div
        class="text-lg font-semibold text-gray-800 mb-4 pb-1 border-b-2 border-blue-500"
      >
        Datos de la cancelación
      </div>

      <div class="flex flex-col gap-4">
        <!-- ¿Quién realiza la cancelación? -->
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
          <label for="tipoCancelador" class="font-medium text-gray-700">
            ¿Quien realiza la cancelación? <span class="text-red-500">*</span>
          </label>
          <div class="col-span-3">
            <p-dropdown
              id="tipoCancelador"
              [options]="tiposCancelacionFiltrados()"
              [(ngModel)]="form.tipoCancelador"
              optionLabel="descripcion"
              optionValue="codigo"
              [filter]="true"
              filterBy="descripcion"
              placeholder="--Seleccione una opción--"
              [style]="{ width: '100%' }"
              appendTo="body"
              [scrollHeight]="'200px'"
              (onChange)="onTipoCanceladorChange()"
            >
            </p-dropdown>
          </div>
        </div>

        <!-- Panel de Persona (legacy: IdentificationViewImpl) -->
        @if (mostrarPanelPersona()) {
          <div
            class="bg-gray-50 dark:bg-gray-800 p-3 rounded border border-gray-200 dark:border-gray-600"
          >
            <div
              class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center mb-2"
            >
              <label class="font-medium text-gray-700">Persona</label>
              <div class="col-span-3 flex flex-wrap gap-2 items-center">
                <!-- Dropdown readonly de tipo de identificación -->
                <p-dropdown
                  [options]="tiposIdentificacion"
                  [ngModel]="
                    personaActual()?.tipoIdentificacion ||
                    'CEDULA DE CIUDADANIA'
                  "
                  [disabled]="true"
                  styleClass="w-full sm:w-48"
                >
                </p-dropdown>
                <!-- Input readonly de número de identificación -->
                <input
                  type="text"
                  pInputText
                  [value]="personaActual()?.numeroIdentificacion || ''"
                  readonly
                  class="w-full sm:w-36"
                />
              </div>
            </div>
            <div
              class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center mb-2"
            >
              <label class="font-medium text-gray-700">1er nombre</label>
              <div class="col-span-1">
                <input
                  type="text"
                  pInputText
                  [value]="
                    personaActual()?.nombre1 ||
                    personaActual()?.nombreCompleto ||
                    ''
                  "
                  readonly
                  class="w-full"
                />
              </div>
              <label class="font-medium text-gray-700">2do nombre</label>
              <div class="col-span-1">
                <input
                  type="text"
                  pInputText
                  [value]="personaActual()?.nombre2 || ''"
                  readonly
                  class="w-full"
                />
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
              <label class="font-medium text-gray-700">1er apellido</label>
              <div class="col-span-1">
                <input
                  type="text"
                  pInputText
                  [value]="personaActual()?.apellido1 || ''"
                  readonly
                  class="w-full"
                />
              </div>
              <label class="font-medium text-gray-700">2do apellido</label>
              <div class="col-span-1">
                <input
                  type="text"
                  pInputText
                  [value]="personaActual()?.apellido2 || ''"
                  readonly
                  class="w-full"
                />
              </div>
            </div>
          </div>
        }

        <!-- Nombre del contacto (solo para BEN, BES, INT) -->
        @if (mostrarNombreContacto()) {
          <div class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
            <label for="nombreContacto" class="font-medium text-gray-700">
              Nombre del contacto <span class="text-red-500">*</span>
            </label>
            <div class="col-span-3">
              <input
                id="nombreContacto"
                type="text"
                pInputText
                [(ngModel)]="form.nombreContacto"
                (change)="
                  form.nombreContacto = sanitizeText(form.nombreContacto)
                "
                [style]="{ width: '100%' }"
                maxlength="60"
              />
            </div>
          </div>
        }

        <!-- Motivo de cancelación (dropdown de TPSC_PARAMETROS_GENERALES) -->
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-center">
          <label for="motivoCancelacion" class="font-medium text-gray-700">
            Motivo de cancelación <span class="text-red-500">*</span>
          </label>
          <div class="col-span-3">
            <p-dropdown
              id="motivoCancelacion"
              [options]="motivosCancelacion()"
              [(ngModel)]="form.codigoMotivoCancelacion"
              optionLabel="descripcion"
              optionValue="codigo"
              placeholder="--Seleccione una opción--"
              [filter]="true"
              filterBy="descripcion"
              [style]="{ width: '100%' }"
              appendTo="body"
              [scrollHeight]="'200px'"
              (onChange)="onMotivoCancelacionChange()"
            >
            </p-dropdown>
          </div>
        </div>

        <!-- Observación (textarea obligatorio según legacy) -->
        <div class="grid grid-cols-1 sm:grid-cols-4 gap-2 items-start">
          <label for="observaciones" class="font-medium text-gray-700 pt-2">
            Observación
          </label>
          <div class="col-span-3">
            <textarea
              id="observaciones"
              pInputTextarea
              [(ngModel)]="form.observaciones"
              (change)="
                form.observaciones = sanitizeTextarea(form.observaciones)
              "
              [rows]="3"
              [style]="{ width: '100%' }"
              maxlength="200"
            >
            </textarea>
          </div>
        </div>
      </div>

      <!-- Botones abajo (estándar nuevo) -->
      <ng-template pTemplate="footer">
        <div
          class="flex flex-wrap justify-end sm:justify-start gap-2 sm:gap-3 w-full"
        >
          <p-button
            label="Cerrar"
            icon="pi pi-times"
            [outlined]="true"
            styleClass="min-h-[44px]"
            (onClick)="visible = false; onClose.emit()"
          >
          </p-button>
          <p-button
            label="Grabar"
            icon="pi pi-check"
            severity="danger"
            [disabled]="!formValido()"
            styleClass="min-h-[44px]"
            (onClick)="confirmar()"
          >
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `
})
export class CancelarCitaDialogComponent implements OnInit {
  // XSS Sanitization - exponer funciones al template
  sanitizeText = sanitizeText;
  sanitizeTextarea = sanitizeTextarea;

  // === Inputs ===
  /** ID de la cita a cancelar */
  citaId = input.required<number>();

  /** Cita completa (para obtener datos del asesor) */
  cita = input<ProgramacionServicio | null>(null);

  /** Datos del asesor (si están disponibles por separado) */
  datosAsesor = input<DatosPersona | null>(null);

  /**
   * Datos del usuario en sesión (programador), poblados por el contenedor
   * desde {@code CitaFacade.cargarDatosProgramador()}, que consume el endpoint
   * {@code /api/servicios/usuario/sesion} vía {@code UsuarioStateService}.
   * Legacy: {@code ApplicationCommonData.getSessionUser().getCdpersona()}.
   */
  datosProgramador = input<DatosPersona | null>(null);

  /**
   * Flag para cancelar citas futuras en programación periódica.
   * Legacy: snCancelPeriodicity (viene de getSnModifyPeriodicity() del padre)
   * Se determina ANTES de abrir este diálogo cuando el usuario responde
   * al mensaje de periodicidad.
   * 'S' = Cancelar todas las citas futuras asociadas
   * 'N' = Solo cancelar esta cita (default)
   */
  cancelarFuturas = input<'S' | 'N'>('N');

  // === Outputs ===
  onClose = output<void>();
  onConfirm = output<CancelarCitaResult>();

  // === Services ===
  private readonly catalogoRepository = inject(CatalogoRepository);

  // === State ===
  visible = true;

  tiposCancelacion = signal<CatalogoOption[]>([]);
  motivosCancelacion = signal<CatalogoOption[]>([]);

  /** Tipos de identificación para el dropdown readonly */
  tiposIdentificacion = [
    { label: 'CEDULA DE CIUDADANIA', value: 'CEDULA DE CIUDADANIA' },
    { label: 'CEDULA DE EXTRANJERIA', value: 'CEDULA DE EXTRANJERIA' },
    { label: 'NIT', value: 'NIT' },
    { label: 'PASAPORTE', value: 'PASAPORTE' }
  ];

  /** Signal del tipo de cancelador seleccionado (para reactividad de computed) */
  tipoCanceladorSeleccionado = signal<string | null>(null);

  /** Signal del motivo de cancelación seleccionado (para reactividad de computed) */
  motivoCancelacionSeleccionado = signal<string | null>(null);

  form = {
    tipoCancelador: null as string | null,
    nombreContacto: '',
    observaciones: '',
    codigoMotivoCancelacion: null as string | null
  };

  // Almacena las descripciones seleccionadas
  private descripcionTipoCancelador = '';
  private descripcionMotivoCancelacion = '';

  // === Computed ===

  /**
   * Filtra los tipos de cancelación según SNAPLICA_CLIENTE del tipo de cita.
   *
   * Legacy (CancelServiceAppointmentInterfaceImpl líneas 811-825):
   * - Si tipoCita.SNAPLICA_CLIENTE='S' → muestra todos con SNACTIVO='S'  
   * - Si tipoCita.SNAPLICA_CLIENTE='N' → filtra los que tienen SNCLIENTE='N'
   *
   * NOTA: El campo SNAPLICA_CLIENTE del tipo de cita determina si ese tipo
   * permite crear citas para clientes. Si es 'S', el diálogo de cancelación
   * muestra TODOS los tipos incluyendo "Cliente anticipado" y "Cliente en sitio".
   *
   * Tipo CAMPO COMPLEMENTARIO (42) tiene SNAPLICA_CLIENTE='S' en la BD,
   * por lo tanto muestra todos los tipos de cancelación (comportamiento correcto según legacy).
   */
  tiposCancelacionFiltrados = computed(() => {
    const todos = this.tiposCancelacion();
    const citaActual = this.cita();

    // Obtener snaplica_cliente del tipo de cita
    // Busca en tipoCita.snaplicaCliente O directamente en snaplicaCliente (para orden-servicio)
    // Si no hay tipoCita, usamos 'N' por defecto (más restrictivo)
    const snaplicaCliente =
      citaActual?.tipoCita?.snaplicaCliente ||
      (citaActual as any)?.snaplicaCliente ||
      'N';

    if (snaplicaCliente === 'S') {
      // Cita aplica cliente: mostrar TODOS los activos
      return todos;
    } else {
      // Cita NO aplica cliente: filtrar solo los que tienen SNCLIENTE='N'
      return todos.filter((t) => t.sncliente === 'N');
    }
  });

  /**
   * Muestra el panel de persona para ASESOR/CONSULTOR, PROGRAMADOR y CLIENTE
   * Legacy: identificationName.setVisible(true)
   */
  mostrarPanelPersona = computed(() => {
    const tipo = this.tipoCanceladorSeleccionado();
    const tipoSeleccionado = this.tiposCancelacionFiltrados().find(
      (t) => t.codigo === tipo
    );
    return (
      esTipoAsesor(tipo, tipoSeleccionado?.descripcion) ||
      esTipoProgramador(tipo, tipoSeleccionado?.descripcion) ||
      esTipoCliente(tipo, tipoSeleccionado?.descripcion)
    );
  });

  /**
   * Retorna los datos de la persona a mostrar según el tipo seleccionado
   * - ASESOR/CONSULTOR: datos del asesor de la cita (extraídos de los campos dsXXXAsesor)
   * - PROGRAMADOR: datos del usuario en sesión (si están disponibles)
   */
  personaActual = computed((): DatosPersona | null => {
    const tipo = this.tipoCanceladorSeleccionado();
    const tipoSeleccionado = this.tiposCancelacionFiltrados().find(
      (t) => t.codigo === tipo
    );

    if (esTipoAsesor(tipo, tipoSeleccionado?.descripcion)) {
      // Extraer datos del asesor de la cita
      const c = this.cita();
      if (c) {
        // Si tenemos los datos individuales, usarlos
        if (c.nmidentificacionAsesor || c.dsnombre1Asesor) {
          return {
            tipoIdentificacion:
              c.dstipoIdentificacionAsesor || 'CEDULA DE CIUDADANIA',
            numeroIdentificacion: c.nmidentificacionAsesor,
            nombre1: c.dsnombre1Asesor,
            nombre2: c.dsnombre2Asesor,
            apellido1: c.dsapellido1Asesor,
            apellido2: c.dsapellido2Asesor,
            nombreCompleto: c.nombreAsesor
          };
        }
        // Fallback: solo nombre completo
        if (c.nombreAsesor) {
          return { nombreCompleto: c.nombreAsesor };
        }
      }
      // Usar datosAsesor si están disponibles por input
      return this.datosAsesor();
    }

    if (esTipoProgramador(tipo, tipoSeleccionado?.descripcion)) {
      return this.datosProgramador();
    }

    if (esTipoCliente(tipo, tipoSeleccionado?.descripcion)) {
      // Extraer datos del cliente/beneficiario de la cita
      // Se usa 'as any' porque estos campos vienen del adaptador de orden-servicio
      const c = this.cita() as any;
      if (c) {
        // Datos del cliente desde la programación
        if (c.dsidentificacionBeneficiario || c.nombreBeneficiario) {
          return {
            tipoIdentificacion:
              c.dstipoIdentificacionBeneficiario || 'CEDULA DE CIUDADANIA',
            numeroIdentificacion: c.dsidentificacionBeneficiario,
            nombreCompleto: c.nombreBeneficiario
          };
        }
      }
      return null;
    }

    return null;
  });

  /**
   * Muestra "Nombre del contacto" para tipos que requieren contacto
   * (Cliente anticipado, Cliente en sitio, Interno, etc.)
   */
  mostrarNombreContacto = computed(() => {
    const tipo = this.tipoCanceladorSeleccionado();
    const tipoSeleccionado = this.tiposCancelacionFiltrados().find(
      (t) => t.codigo === tipo
    );
    return requiereNombreContacto(tipo, tipoSeleccionado?.descripcion);
  });

  /**
   * Validación del formulario
   * - Tipo cancelador siempre requerido
   * - Motivo de cancelación siempre requerido
   * - Nombre contacto requerido para BEN, BES, INT
   */
  formValido = computed(() => {
    // Tipo cancelador siempre requerido (usar signal para reactividad)
    if (!this.tipoCanceladorSeleccionado()) {
      return false;
    }

    // Motivo de cancelación siempre requerido (usar signal para reactividad)
    if (!this.motivoCancelacionSeleccionado()) {
      return false;
    }

    // Nombre contacto requerido para BEN, BES, INT
    if (this.mostrarNombreContacto() && !this.form.nombreContacto?.trim()) {
      return false;
    }

    return true;
  });

  // === Lifecycle ===

  async ngOnInit(): Promise<void> {
    await this.cargarCatalogos();
  }

  // === Methods ===

  private async cargarCatalogos(): Promise<void> {
    try {
      const [tipos, motivos] = await Promise.all([
        this.catalogoRepository.getTiposCancelacionCita(),
        this.catalogoRepository.getMotivosCancelacion()
      ]);

      this.tiposCancelacion.set(tipos);
      this.motivosCancelacion.set(motivos);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando catálogos:', error);
      }
    }
  }

  onTipoCanceladorChange(): void {
    // Actualizar signal para reactividad de computed
    this.tipoCanceladorSeleccionado.set(this.form.tipoCancelador);

    // Buscar descripción del tipo seleccionado
    const tipo = this.tiposCancelacion().find(
      (t) => t.codigo === this.form.tipoCancelador
    );
    this.descripcionTipoCancelador = tipo?.descripcion || '';

    // Limpiar nombre de contacto si ya no es necesario
    if (!this.mostrarNombreContacto()) {
      this.form.nombreContacto = '';
    }
  }

  onMotivoCancelacionChange(): void {
    // Actualizar signal para reactividad de computed
    this.motivoCancelacionSeleccionado.set(this.form.codigoMotivoCancelacion);

    // Buscar descripción del motivo seleccionado
    const motivo = this.motivosCancelacion().find(
      (m) => m.codigo === this.form.codigoMotivoCancelacion
    );
    this.descripcionMotivoCancelacion = motivo?.descripcion || '';
  }

  confirmar(): void {
    if (!this.formValido()) {
      return;
    }

    // Legacy CancelServiceAppointmentInterfaceImpl.getDTO():
    //   ASE / CON  → cdpersona del asesor de la cita
    //   BEN / BES + cita ADMIN → cdpersona del beneficiario de la cita
    //     (legacy: setPersonaDataOnFields(benef.getCdpersona()))
    //   PRO / INT (y BEN/BES en cita SERVICIO) → CDPERSONA del usuario en sesión,
    //     que resuelve el backend a partir del DNI SSO.
    const tipoUpper = this.form.tipoCancelador?.toUpperCase();
    const cita = this.cita();
    const esCitaAdmin = (cita?.tipoCita?.dsusoCita || '').toUpperCase() === 'ADMIN';
    let consecutivoUsuario: number | undefined;
    if (tipoUpper === 'ASE' || tipoUpper === 'CON') {
      consecutivoUsuario = cita?.cdpersonaAsesor || undefined;
    } else if ((tipoUpper === 'BEN' || tipoUpper === 'BES') && esCitaAdmin) {
      consecutivoUsuario = cita?.cdpersonaBeneficiario || undefined;
    } else {
      consecutivoUsuario = undefined;
    }

    const result: CancelarCitaResult = {
      citaId: this.citaId(),
      codigoTipoCancelador: this.form.tipoCancelador!,
      descripcionTipoCancelador: this.descripcionTipoCancelador,
      nombreContacto: this.mostrarNombreContacto()
        ? this.form.nombreContacto
        : undefined,
      observaciones: this.form.observaciones || undefined,
      motivoCancelacion: this.descripcionMotivoCancelacion,
      cancelarFuturas: this.cancelarFuturas(),
      consecutivoUsuario
    };

    this.onConfirm.emit(result);
    this.visible = false;
  }
}
